import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardCheck,
  Dna,
  FlaskConical,
  ShieldAlert,
  Sparkles,
  Stethoscope,
} from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { patientQueries } from '@/features/patient-record/queries';
import { useNoFindingsStore } from '@/features/patient-record/stores/noFindingsStore';

import { CaseStateCell } from './CaseStateCell';
import { VARIANT_SEVERITY_ORDER } from './constants';
import { VariantsTooltip } from './VariantsTooltip';
import { FeaturesTooltip } from './FeaturesTooltip';
import { DiagnosesTooltip } from './DiagnosesTooltip';
import { OrdersTooltip } from './OrdersTooltip';

interface GeneticSummaryStripProps {
  readonly guid: string;
}

/**
 * V1 case-state strip — six cells in a fixed grid (variants,
 * phenotypes, diagnoses, orders, reply needed, consent pending). Each
 * clinical cell reveals an underlying-records tooltip on hover; warn
 * cells (reply / consent) stay static. Hides itself when the patient
 * has no clinical data on file.
 */
export function GeneticSummaryStrip({
  guid,
}: GeneticSummaryStripProps): ReactElement | null {
  const { t } = useTranslation();
  const variantsQuery = useQuery(patientQueries.geneticVariants(guid));
  const featuresQuery = useQuery(patientQueries.features(guid));
  const noFindings = useNoFindingsStore((s) => s.byGuid[guid] ?? false);
  const diagnosesQuery = useQuery(patientQueries.diagnoses(guid));
  const ordersQuery = useQuery(patientQueries.testOrders(guid));
  const consentsQuery = useQuery(patientQueries.consents(guid));
  const questionnairesQuery = useQuery(patientQueries.questionnaires(guid));

  // While any query is still loading on first render, show a skeleton.
  // Once at least one resolves, render the strip with whatever has loaded —
  // failing endpoints fall through to empty arrays so the strip stays
  // useful while sibling sections are still being wired up on the BE.
  const isInitialLoading =
    variantsQuery.isPending &&
    featuresQuery.isPending &&
    diagnosesQuery.isPending &&
    ordersQuery.isPending &&
    consentsQuery.isPending &&
    questionnairesQuery.isPending;

  if (isInitialLoading) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label={t('patient.geneticSummary.loading')}
        className="bg-card border-b border-border px-8 py-1.5"
      >
        <Skeleton className="h-6 w-72" />
      </div>
    );
  }

  const rawVariants = variantsQuery.data ?? [];
  const variants = [...rawVariants].sort((a, b) => {
    const aOrder =
      a.interpretation === null ? 5 : VARIANT_SEVERITY_ORDER[a.interpretation];
    const bOrder =
      b.interpretation === null ? 5 : VARIANT_SEVERITY_ORDER[b.interpretation];
    return aOrder - bOrder;
  });
  // The Phenotypes section filters `/features` to `type === 'PHENOTYPE'`
  // (the endpoint multiplexes cancer / disorder / mode-of-inheritance
  // records too). Apply the same filter here so the strip's count and
  // tooltip match the table row-for-row.
  const features = (featuresQuery.data ?? []).filter(
    (f) => f.kind === 'PHENOTYPE' || f.kind === null,
  );
  const diagnoses = diagnosesQuery.data ?? [];
  const orders = ordersQuery.data ?? [];
  const consents = consentsQuery.data ?? [];
  const questionnaires = questionnairesQuery.data ?? [];

  const variantCount = variants.length;
  const pCount = variants.filter((v) => v.interpretation === 'Pathogenic').length;
  const lpCount = variants.filter(
    (v) => v.interpretation === 'Likely Pathogenic',
  ).length;
  const vusCount = variants.filter((v) => v.interpretation === 'VUS').length;
  const variantBreakdownParts: string[] = [];
  if (pCount > 0)
    variantBreakdownParts.push(
      t('patient.geneticSummary.pathogenicShort', { count: pCount }),
    );
  if (lpCount > 0)
    variantBreakdownParts.push(
      t('patient.geneticSummary.likelyPathogenicShort', { count: lpCount }),
    );
  if (vusCount > 0)
    variantBreakdownParts.push(
      t('patient.geneticSummary.vusShort', { count: vusCount }),
    );
  // Every strip cell keeps its fixed title in the small-caps caption; the
  // per-status breakdown rides inline beside the count as a suffix so the
  // title never gets swapped out for the breakdown once data is entered
  // (PTV2-1290). Mirrors the Orders cell, which already worked this way.
  const toSuffix = (parts: readonly string[]): string | undefined =>
    parts.length > 0 ? `· ${parts.join(' · ')}` : undefined;

  const presentFeatures = features.filter((f) => f.status === 'present');
  const absentFeatures = features.filter((f) => f.status === 'absent');
  const unknownFeatures = features.filter((f) => f.status === 'unknown');
  const totalFeatures = features.length;
  const featureBreakdownParts = noFindings
    ? [t('patient.geneticSummary.noFindings')]
    : totalFeatures > 0
      ? [
          t('patient.geneticSummary.present', {
            count: presentFeatures.length,
          }),
          t('patient.geneticSummary.absent', { count: absentFeatures.length }),
          ...(unknownFeatures.length > 0
            ? [
                t('patient.geneticSummary.unknown', {
                  count: unknownFeatures.length,
                }),
              ]
            : []),
        ]
      : [];

  const confirmedDiagnoses = diagnoses.filter(
    (d) => d.status === 'Confirmed' || d.status === 'Molecular Diagnosis',
  );
  const suspectedDiagnoses = diagnoses.filter((d) => d.status === 'Suspected');
  const totalDiagnoses = confirmedDiagnoses.length + suspectedDiagnoses.length;
  const diagnosisBreakdownParts: string[] = [];
  if (confirmedDiagnoses.length > 0)
    diagnosisBreakdownParts.push(
      t('patient.geneticSummary.confirmedShort', {
        count: confirmedDiagnoses.length,
      }),
    );
  if (suspectedDiagnoses.length > 0)
    diagnosisBreakdownParts.push(
      t('patient.geneticSummary.suspectedShort', {
        count: suspectedDiagnoses.length,
      }),
    );

  // "Pending" = open lifecycle states: ORDERED + SAMPLE_SENT. RESULTED
  // is counted separately ("awaiting review"). REVIEWED and CANCELLED
  // are closed and don't contribute to the chip count.
  const activeOrders = orders.filter(
    (o) => o.status === 'ORDERED' || o.status === 'SAMPLE_SENT',
  );
  const resultedOrders = orders.filter((o) => o.status === 'RESULTED');

  const pendingConsents = consents.filter((c) => c.status === 'PENDING').length;
  const pendingQuestionnaires = questionnaires.filter(
    (q) => q.status === 'SENT' || q.status === 'OPENED',
  ).length;

  const hasAnyData =
    variantCount > 0 ||
    totalFeatures > 0 ||
    totalDiagnoses > 0 ||
    activeOrders.length > 0 ||
    resultedOrders.length > 0 ||
    pendingConsents > 0 ||
    pendingQuestionnaires > 0 ||
    noFindings;

  if (!hasAnyData) {
    return (
      <div className="bg-card border-b border-border px-8 py-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Dna className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{t('patient.geneticSummary.noData')}</span>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        aria-label={t('patient.geneticSummary.ariaLabel')}
        className="bg-card border-b border-border px-6 grid grid-cols-3 md:grid-cols-6 divide-x divide-border/60"
      >
        <CaseStateCell
          icon={<Dna className="h-4 w-4" aria-hidden="true" />}
          value={String(variantCount)}
          valueSuffix={toSuffix(variantBreakdownParts)}
          label={t('patient.geneticSummary.variantsShort')}
          tooltip={
            variantCount > 0 ? (
              <VariantsTooltip variants={variants} totalCount={variantCount} />
            ) : undefined
          }
        />
        <CaseStateCell
          icon={<Sparkles className="h-4 w-4" aria-hidden="true" />}
          value={noFindings ? '0' : String(totalFeatures)}
          valueSuffix={toSuffix(featureBreakdownParts)}
          label={t('patient.geneticSummary.hpoFeaturesShort')}
          tooltip={
            !noFindings && totalFeatures > 0 ? (
              <FeaturesTooltip
                present={presentFeatures}
                absent={absentFeatures}
                unknown={unknownFeatures}
              />
            ) : undefined
          }
        />
        <CaseStateCell
          icon={<Stethoscope className="h-4 w-4" aria-hidden="true" />}
          value={String(totalDiagnoses)}
          valueSuffix={toSuffix(diagnosisBreakdownParts)}
          label={t('patient.geneticSummary.omimDisordersShort')}
          tooltip={
            totalDiagnoses > 0 ? (
              <DiagnosesTooltip
                confirmed={confirmedDiagnoses}
                suspected={suspectedDiagnoses}
              />
            ) : undefined
          }
        />
        <CaseStateCell
          icon={<FlaskConical className="h-4 w-4" aria-hidden="true" />}
          value={String(activeOrders.length)}
          valueSuffix={toSuffix(
            resultedOrders.length > 0
              ? [
                  t('patient.geneticSummary.ordersResulted', {
                    count: resultedOrders.length,
                  }),
                ]
              : [],
          )}
          label={t('patient.geneticSummary.ordersPending')}
          tooltip={
            activeOrders.length + resultedOrders.length > 0 ? (
              <OrdersTooltip orders={[...activeOrders, ...resultedOrders]} />
            ) : undefined
          }
        />
        <CaseStateCell
          icon={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
          value={String(pendingQuestionnaires)}
          label={t('patient.geneticSummary.replyNeeded')}
          tone={pendingQuestionnaires > 0 ? 'warn' : 'default'}
        />
        <CaseStateCell
          icon={<ShieldAlert className="h-4 w-4" aria-hidden="true" />}
          value={String(pendingConsents)}
          label={t('patient.geneticSummary.consentPending')}
          tone={pendingConsents > 0 ? 'warn' : 'default'}
        />
      </div>
    </TooltipProvider>
  );
}
